import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQrcode } from "@fortawesome/free-solid-svg-icons";
import Button from "../../../../components/Button";
import Select from "../../../../components/Inputs/Select";
import { useGroups } from "../../General/OrganisationLevelGroups/context";
import { useAccess } from "../../../../utils/hooks/access";
import { useConfigurationActions } from "../api/useConfiguration";
import { useConfigurationModule } from "../hooks/useConfigurationModule";

// Used when the main company has no `qrCode` preference
export const DEFAULT_QR_PRINT_CELLS = [
  { field: "location", format: "code-description" },
  null,
  { field: "orgLevel", format: "description" },
  null,
  { field: "description" },
  null,
];

// Main company preference key holding the QR print layout
export const QR_PRINT_PREFERENCE_KEY = "qrCode";

const EMPTY_SLOT = "__empty__";

const QR_PRINT_SLOT_COUNT = 6;

// Formats each field supports, an empty list means the field has no format
const QR_PRINT_FIELD_FORMATS = {
	location: ["code", "description", "code-description"],
	orgLevel: [
		"code",
		"description",
		"code-description",
		"glSegment",
		"glSegment-description",
	],
	description: [],
	address: [],
};

const QR_PRINT_FIELDS = Object.keys(QR_PRINT_FIELD_FORMATS);

// Format picked when a field is selected without one (matches the old printout)
const QR_PRINT_DEFAULT_FORMAT = {
	location: "code-description",
	orgLevel: "description",
};

const isQrPrintField = (value) => QR_PRINT_FIELDS.includes(value);

const createQrPrintCell = (field, format) => {
	const formats = QR_PRINT_FIELD_FORMATS[field];
	if (!formats.length) {
		return { field };
	}
	return {
		field,
		format: formats.includes(format) ? format : QR_PRINT_DEFAULT_FORMAT[field],
	};
};

// Coerces stored cells into exactly six slots. Unknown and repeated fields become empty slots,
// invalid formats fall back to the field default. Missing config resolves to the default layout.
const normalizeQrPrintCells = (cells) => {
	if (!Array.isArray(cells)) {
		return DEFAULT_QR_PRINT_CELLS;
	}

	const used = new Set();
	return Array.from({ length: QR_PRINT_SLOT_COUNT }, (_, index) => {
		const cell = cells[index];
		if (!isQrPrintField(cell?.field) || used.has(cell.field)) {
			return null;
		}
		used.add(cell.field);
		return createQrPrintCell(cell.field, cell.format);
	});
};

function Slot({ index, cell, usedFields, fieldLabels, disabled, onChange }) {
	const { t } = useTranslation();
	const formats = cell ? QR_PRINT_FIELD_FORMATS[cell.field] : [];

	return (
		<div className="border rounded p-3">
			<div className="text-sm font-weight-bold text-muted mb-2">
				{t("qr-print-slot", { number: index + 1 })}
			</div>

			<Select
				className="w-100"
				value={cell?.field || EMPTY_SLOT}
				onChange={(value) => onChange(isQrPrintField(value) ? createQrPrintCell(value) : null)}
				disabled={disabled}
			>
				<Select.Option value={EMPTY_SLOT}>{t("qr-print-empty-slot")}</Select.Option>
				{QR_PRINT_FIELDS.map((field) => (
					<Select.Option
						key={field}
						value={field}
						disabled={field !== cell?.field && usedFields.has(field)}
					>
						{fieldLabels[field]}
					</Select.Option>
				))}
			</Select>

			{cell && formats.length > 0 && (
				<Select
					className="w-100 mt-2"
					value={cell.format}
					onChange={(format) => onChange(createQrPrintCell(cell.field, format))}
					disabled={disabled}
				>
					{formats.map((format) => (
						<Select.Option key={format} value={format}>
							{t(format)}
						</Select.Option>
					))}
				</Select>
			)}
		</div>
	);
}

function QrCodeConfiguration() {
	const { t } = useTranslation();
	const { groups } = useGroups();
	const { hasAccess: canSave } = useAccess("settings.configuration.canCreate");

	const config = useConfigurationModule(QR_PRINT_PREFERENCE_KEY);
	const { submit, submitting } = useConfigurationActions(QR_PRINT_PREFERENCE_KEY);

	const [cells, setCells] = useState(() => normalizeQrPrintCells(config?.cells));

	useEffect(() => {
		setCells(normalizeQrPrintCells(config?.cells));
	}, [config]);

	const fieldLabels = useMemo(() => {
		const group = groups?.find(({ useInClockIn }) => useInClockIn);
		return {
			location: t("location"),
			orgLevel: group?.description || t("organization-level"),
			description: t("description"),
			address: t("address"),
		};
	}, [groups, t]);

	const usedFields = useMemo(
		() => new Set(cells.filter(Boolean).map(({ field }) => field)),
		[cells],
	);

	const onSlotChange = (index, cell) => {
		setCells((current) => current.map((item, i) => (i === index ? cell : item)));
	};

	const saveCells = async (nextCells) => {
		try {
			await submit({
				entity: "company",
				config: { cells: nextCells },
			});
			toast.success(t("qr-print-configuration-saved"));
		} catch (err) {
			toast.error(t("could-not-save-qr-print-configuration"));
		}
	};

	const onSave = () => saveCells(cells);

	const onReset = async () => {
		setCells(DEFAULT_QR_PRINT_CELLS);
		await saveCells(DEFAULT_QR_PRINT_CELLS);
	};

	return (
		<Card className="h-100">
			<CardHeader>
				<Row>
					<Col xs="6" className="pl-4 d-flex flex-column">
						<h3 className="mb-0">{t("qr-code-print")}</h3>
						<span className="text-sm text-muted">
							{t("qr-print-configuration-description")}
						</span>
					</Col>

					<Col className="text-right" xs="6">
						<Button
							className="btn-round px-3"
							size="sm"
							onClick={onReset}
							disabled={!canSave || submitting}
						>
							{t("reset-to-default")}
						</Button>

						<Button
							className="btn-dark px-3 btn-round btn-icon"
							size="sm"
							loading={submitting}
							disabled={!canSave || submitting}
							onClick={onSave}
						>
							{t("save")}
						</Button>
					</Col>
				</Row>
			</CardHeader>

			<CardBody>
				<div className="border rounded p-4 mx-auto" style={{ maxWidth: 800 }}>
					<div className="d-flex flex-column align-items-center bg-secondary rounded p-3 mb-4 text-muted">
						<FontAwesomeIcon icon={faQrcode} size="4x" className="mb-2" />
						<span className="text-sm">{t("qr-print-fixed-header")}</span>
					</div>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							gap: "1rem",
						}}
					>
						{cells.map((cell, index) => (
							<Slot
								key={index}
								index={index}
								cell={cell}
								usedFields={usedFields}
								fieldLabels={fieldLabels}
								disabled={!canSave}
								onChange={(value) => onSlotChange(index, value)}
							/>
						))}
					</div>
				</div>
			</CardBody>
		</Card>
	);
}

export default QrCodeConfiguration;
